import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Fun loading messages that rotate while waiting for API calls
 */
const DEFAULT_LOADING_MESSAGES = [
  'Analyzing your goals...',
  'Finding patterns...',
  'Calling on AI wisdom...',
  'Crafting your plan...',
  'Crunching the numbers...',
  'Organizing priorities...',
  'Making magic happen...',
  'Almost there...',
];

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  isLoading: boolean;
  /** Custom loading messages to rotate through (optional) */
  messages?: string[];
  /** Time in milliseconds between message rotations (default: 2000ms) */
  rotationInterval?: number;
  /** Size of the icon in pixels (default: 48) */
  iconSize?: number;
}

/**
 * LoadingOverlay Component
 *
 * Displays a full-screen overlay with rotating fun messages during API calls.
 * Positioned over the top of screens while waiting for data.
 */
export function LoadingOverlay({
  isLoading,
  messages = DEFAULT_LOADING_MESSAGES,
  rotationInterval = 2000,
  iconSize = 48,
}: LoadingOverlayProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  console.log('[LoadingOverlay] Rendered. isLoading:', isLoading);

  // Rotate through messages
  useEffect(() => {
    if (!isLoading) {
      setCurrentMessageIndex(0);
      return;
    }

    console.log('[LoadingOverlay] Showing loading overlay with', messages.length, 'messages');

    const interval = setInterval(() => {
      // Fade out
      setFadeIn(false);

      // After fade out, change message and fade in
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
        setFadeIn(true);
      }, 300);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [isLoading, messages.length, rotationInterval]);

  if (!isLoading) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid #e5e7eb',
          minWidth: '320px',
          maxWidth: '90%',
        }}
      >
        <div className="flex flex-col items-center justify-center space-y-6">
          {/* Animated Icon */}
          <div className="relative">
            <Sparkles
              size={iconSize}
              className="text-primary-600 animate-pulse"
            />
            <div
              className="absolute inset-0 animate-spin"
              style={{ animationDuration: '3s' }}
            >
              <Sparkles
                size={iconSize}
                className="text-primary-400 opacity-50"
              />
            </div>
          </div>

          {/* Rotating Message */}
          <div
            className="text-center min-h-[2rem] flex items-center justify-center relative w-full"
            style={{
              minHeight: '3rem',
            }}
          >
            <p
              className="text-lg font-medium text-gray-900 absolute inset-0 flex items-center justify-center px-4"
              style={{
                transition: 'opacity 0.6s ease-in-out',
                opacity: fadeIn ? 1 : 0,
              }}
            >
              {messages[currentMessageIndex]}
            </p>
          </div>

          {/* Progress indicator dots */}
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-primary-600"
                style={{
                  opacity: 0.3,
                  animation: `pulse 1.5s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
}
